import ormaApi from './OrmaApi';

export const getMemberById = async (memberId) => {
  try {
    const response = await ormaApi.get(`/api/members/${memberId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching member:', error);
    return null;
  }
};

/**
 * Create a new member
 */
export const createMember = async (memberData) => {
  try {
    const response = await ormaApi.post('/api/members/create', memberData);
    return response.data;
  } catch (error) {
    console.error('Error creating member:', error);
    throw error;
  }
};

/**
 * Update a member by ID
 * Supports MongoDB update operators: $set, $push, $pull, $addToSet, $inc, etc.
 * If no operators are provided, assumes $set
 * 
 * Examples:
 * - updateMember(id, { name: 'John' }) // Uses $set automatically
 * - updateMember(id, { $set: { name: 'John' }, $push: { devices: 'scale' } })
 * - updateMember(id, { $pull: { emDates: '2024-01-01' } })
 * 
 */
const sanitizeKeys = (obj) => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(sanitizeKeys);
  }

  return Object.keys(obj).reduce((acc, key) => {
    const newKey = key.replace(/\$/g, '__DLR__');
    acc[newKey] = sanitizeKeys(obj[key]);
    return acc;
  }, {});
};

export const updateMember = async (memberId, updateOperations, arrayFilters) => {
  try {
    const sanitizedUpdates = sanitizeKeys(updateOperations);
    const body = {
      updates: sanitizedUpdates,
      arrayFilters,
    };
    const response = await ormaApi.put(`/api/members/${memberId}`, body);
    return response.data;
  } catch (error) {
    console.error('Error updating member:', error);
    throw error;
  }
};

export const fetchMembersWithOptions = async (
  {
    query = null,
    projection = null,
    sortOption = null,
    limit = null,
    skip = null
  }) => {
  try {
    const body = {};

    if (query && Object.keys(query).length > 0) {
      body.query = query;
    }

    if (projection && Object.keys(projection).length > 0) {
      body.projection = projection;
    }

    if (sortOption && Object.keys(sortOption).length > 0) {
      body.sort = sortOption;
    }

    if (limit) {
      body.limit = limit;
    }

    if (skip) {
      body.skip = skip;
    }

    const response = await ormaApi.post('/api/members', body);

    return {
      data: response.data.members || [],
      nTot: response.data.count || 0,
    };
  } catch (error) {
    console.error('Error fetching members with options:', error);
    throw error;
  }
};

export const fetchMembersForBillingReports = async (
  {
    ymRef,
    query = null,
    projection = null,
    sortOption = null,
    limit = null,
    skip = null,
  }) => {
  try {
    const body = { ymRef };

    if (query && Object.keys(query).length > 0) {
      body.query = query;
    }

    if (projection && Object.keys(projection).length > 0) {
      body.projection = projection;
    }

    if (sortOption && Object.keys(sortOption).length > 0) {
      body.sort = sortOption;
    }

    if (limit) {
      body.limit = limit;
    }

    if (skip) {
      body.skip = skip;
    }

    const response = await ormaApi.post(
      '/api/members/billing',
      body,
    );

    return {
      data: response.data.members || [],
      nTot: response.data.count || 0,
    };
  } catch (error) {
    console.error('Error fetching billing members with options:', error);
    throw error;
  }
};

export async function fetchMemberCalls(memberId) {
  try {
    const response = await ormaApi.get(`/api/members/${memberId}/calls`);
    return response.data;
  } catch (error) {
    console.error('Error fetching member calls:', error);
    return [];
  }
}

/**
 * Process member readings
 * Processes all member measurement data and returns the full processed member
 * @param {string} memberId - Member ID
 * @param {string} ymRef - Optional year-month reference (YYYYMM format)
 */
export const processReadings = async (memberId, ymRef = null) => {
  try {
    const url = ymRef
      ? `/api/members/${memberId}/process-readings?ymRef=${ymRef}`
      : `/api/members/${memberId}/process-readings`;
    const response = await ormaApi.get(url);
    return response.data;
  } catch (error) {
    console.error('Error processing readings:', error);
    throw error;
  }
};

/**
 * Apply clinical rules to a member
 * Analyzes member data and returns clinical alerts, diagnosis codes, procedure codes, and risk scores
 * @param {string} memberId - Member ID
 * @param {string[]} measureTypes - Array of measure types to analyze (e.g., ['systolicBPData', 'bloodSugarData'])
 */
export const applyClinicalRules = async (memberId, measureTypes = []) => {
  try {
    const response = await ormaApi.post(`/api/members/${memberId}/clinical-rules`, {
      measureTypes,
    });
    return response.data;
  } catch (error) {
    console.error('Error applying clinical rules:', error);
    throw error;
  }
};

/**
 * Select derived fields for a member
 * Processes member readings and returns calculated fields
 */
export const selectDerivedFields = async (memberId) => {
  try {
    const response = await ormaApi.get(`/api/members/${memberId}/derived-fields`);
    return response.data;
  } catch (error) {
    console.error('Error selecting derived fields:', error);
    throw error;
  }
};

/**
 * Generate a member access URL with JWT token
 * @param {string} phone - Member's phone number
 * @param {string} pathName - Optional pathname for specific portal section
 * @returns {Promise<string>} - Member access URL
 */
export const getMemberAccessURL = async (phone, pathName = null) => {
  try {
    const response = await ormaApi.post('/api/member-access/generate', {
      phone,
      pathName,
    });
    return response.data.url;
  } catch (error) {
    console.error('Error generating member access URL:', error);
    throw error;
  }
};